import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Users, Briefcase, DollarSign, TrendingUp, Calendar, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

export default function CollectivePortal() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeOpportunities: 0,
    totalDistributed: 0,
    activeCampaigns: 0,
    pendingDistributions: 0,
    monthlyGrowth: 0,
  });
  const [organization, setOrganization] = useState<any>(null);

  useEffect(() => {
    loadCollectiveData();
  }, []);

  const loadCollectiveData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's organization
      const { data: membership } = await supabase
        .from("organization_members")
        .select("*, organizations(*)")
        .eq("user_id", user.id)
        .single();

      if (!membership?.organizations) {
        toast({
          title: "No Organization",
          description: "You are not a member of any collective.",
          variant: "destructive",
        });
        navigate("/dashboard");
        return;
      }

      setOrganization(membership.organizations);

      // Get total members count
      const { count: memberCount } = await supabase
        .from("organization_athletes")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", membership.organizations.id)
        .eq("status", "active");

      // Get active opportunities (campaigns)
      const { count: opportunityCount } = await supabase
        .from("campaigns")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", membership.organizations.id)
        .eq("status", "active");

      // Get total distributed value from active deals
      const { data: deals } = await supabase
        .from("deals")
        .select("amount")
        .eq("organization_id", membership.organizations.id)
        .eq("status", "active");

      const totalDistributed = deals?.reduce((sum, d) => sum + Number(d.amount), 0) || 0;

      // Get active campaigns
      const { count: campaignCount } = await supabase
        .from("campaigns")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", membership.organizations.id)
        .in("status", ["planning", "active"]);

      setStats({
        totalMembers: memberCount || 0,
        activeOpportunities: opportunityCount || 0,
        totalDistributed,
        activeCampaigns: campaignCount || 0,
        pendingDistributions: 0,
        monthlyGrowth: 12.5, // Placeholder
      });
    } catch (error) {
      console.error("Error loading collective data:", error);
      toast({
        title: "Error",
        description: "Failed to load collective dashboard",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-muted-foreground">Loading collective dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{organization?.name}</h1>
          <p className="text-muted-foreground">Collective Dashboard</p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Members</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalMembers}</div>
              <p className="text-xs text-muted-foreground">Active athletes</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Opportunities</CardTitle>
              <Briefcase className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeOpportunities}</div>
              <p className="text-xs text-muted-foreground">Available to members</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Distributed</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats.totalDistributed.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">To members</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Campaigns</CardTitle>
              <Award className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeCampaigns}</div>
              <p className="text-xs text-muted-foreground">Running programs</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Growth</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">+{stats.monthlyGrowth}%</div>
              <p className="text-xs text-muted-foreground">vs last month</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Distributions</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pendingDistributions}</div>
              <p className="text-xs text-muted-foreground">Awaiting approval</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks for collective management</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4">
            <Button onClick={() => navigate("/collective/members")}>
              <Users className="mr-2 h-4 w-4" />
              Manage Members
            </Button>
            <Button onClick={() => navigate("/collective/opportunities")} variant="outline">
              <Briefcase className="mr-2 h-4 w-4" />
              Post Opportunity
            </Button>
            <Button onClick={() => navigate("/collective/distribution")} variant="outline">
              <DollarSign className="mr-2 h-4 w-4" />
              Distribute Funds
            </Button>
            <Button onClick={() => navigate("/collective/analytics")} variant="outline">
              <TrendingUp className="mr-2 h-4 w-4" />
              View Analytics
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
